import { useEffect } from 'react';
import { withStyles } from 'vopay';
import { useTranslation } from 'react-i18next';
import { Button, DetailRow, DetailsCard, Title } from 'UI';
import StepShell from '@/components/Entities/Accounts/AccountCreation/StepShell/StepShell';
import useSettingsFormConfig from './useSettingsFormConfig';
import useInheritedSettingsRows from './useInheritedSettingsRows';
import type { ParentAccountOption } from './parentAccountOptions';
import type { SettingsFormValues } from './types';

import styles from './SettingsStep.module.scss';

const cn = withStyles(styles);

type SettingsStepProps = {
  steps: string[];
  currentStep: number;
  // The Details step's selected country — the billing-package options are refetched for it.
  country: string;
  /** The parent picked on the Details step; set, the step shows what the sub-account inherits instead of fields. */
  parentAccount: ParentAccountOption | null;
  nextStep: () => void;
  prevStep: () => void;
  onValuesChange: (values: SettingsFormValues) => void;
};

export default function SettingsStep({
  steps,
  currentStep,
  country,
  parentAccount,
  nextStep,
  prevStep,
  onValuesChange,
}: SettingsStepProps) {
  const { t } = useTranslation('translations', { keyPrefix: 'PartnerAccountCreation.Settings' });
  const { t: tInherited } = useTranslation('translations', { keyPrefix: 'PartnerAccountCreation.Inherited' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });

  const { ctx, renderFormElements } = useSettingsFormConfig({
    country,
    isSubAccountMode: parentAccount !== null,
    onSubmit: nextStep,
  });

  const inheritedRows = useInheritedSettingsRows(parentAccount);

  useEffect(() => {
    onValuesChange(ctx.values);
  }, [ctx.values]);

  return (
    <form onSubmit={ctx.handleSubmit} className={cn('form')}>
      <StepShell
        title={t('title', { defaultValue: 'Settings' })}
        description={
          parentAccount
            ? t('subAccountDescription', { defaultValue: 'The sub-account takes its settings from its parent.' })
            : t('description', { defaultValue: 'Set up how the account will use VoPay services.' })
        }
        steps={steps}
        currentStep={currentStep}
        footer={
          <>
            <Button colorScheme="secondary" type="button" onClick={prevStep}>
              {tGlobal('Buttons.back', { defaultValue: 'Back' })}
            </Button>

            <Button colorScheme="primary" type="submit">
              {tGlobal('Buttons.next', { defaultValue: 'Next' })}
            </Button>
          </>
        }
      >
        {parentAccount ? (
          <DetailsCard data-testid="inheritedSettings">
            <Title level="4">
              {tInherited('title', {
                defaultValue: 'Inherited from {{parentName}}',
                parentName: parentAccount.accountName,
              })}
            </Title>

            {inheritedRows.map(({ label, value }) => (
              <DetailRow key={label} label={label} value={value || '-'} />
            ))}
          </DetailsCard>
        ) : (
          <div className={cn('settingsList')}>{renderFormElements()}</div>
        )}

        <p className={cn('inheritanceNote')}>
          {parentAccount
            ? tInherited('note', {
                defaultValue: 'These settings are inherited from the parent account and can be managed after creation.',
              })
            : t('inheritanceNote', {
                defaultValue:
                  "The account inherits your service permissions as its defaults. They can be managed afterwards through the account's View Permissions.",
              })}
        </p>
      </StepShell>
    </form>
  );
}
